import React from "react";
import { Box, Text, Button, Grid, TextInput, Select } from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { IconClearAll, IconSearch } from "@tabler/icons-react";
import type { IFiltroGestionArchivosVistaProps } from "../interfaces";
import dayjs from "dayjs";
import { useAutenticacion } from "../../../globales/contextos/authContext";
import { EnumTipoTarea } from "../../Tareas/enums/enumTipoTarea";
import { esUsuarioInterno, puedeVerFormatosNbs, soloVisitaInstalacion } from "../utils/permisosGestionArchivos";

const FiltroGestionArchivosVista: React.FC<IFiltroGestionArchivosVistaProps> = ({
    consultarVisitasCliente,
    entidadFiltro,
    manejarCambioValorFiltro,
    limpiarFiltro,
    // clientes,
    Proyectos,
    tiposTarea
}) => {

    const { usuario } = useAutenticacion();
    const esAdmin = esUsuarioInterno(usuario);
    const tipoTareaFijo = soloVisitaInstalacion(usuario);
    // Sin permiso NBS no se ofrece Visita Instalación (esas visitas no se listan).
    const tiposTareaDisponibles = puedeVerFormatosNbs(usuario)
        ? tiposTarea
        : tiposTarea.filter(t => t.Id !== EnumTipoTarea.VisitaInstalacion);

    const proyectosFiltrados = Proyectos.filter(p => {
        const hasIdent = p.Cliente?.Identificacion?.trim();
        if (!hasIdent) return false;
        if (esAdmin) return true;
        return p.Cliente?.Identificacion?.trim() === usuario?.Identificacion?.trim();
    });

    return (
        <Box pb="sm">
            <Grid gutter="sm" >
                <Grid.Col span={{ base: 12, md: 3 }}>
                    <DatePickerInput
                        type="range"
                        size="xs"
                        required
                        valueFormat="MMMM D, YYYY"
                        label="Rango de Visita"
                        placeholder="Seleccione Fecha Inicio y Fecha Fin"
                        locale="es"
                        // Mantine espera [Date | null, Date | null] para el tipo "range"
                        value={[
                            entidadFiltro.FechaInicio ? new Date(entidadFiltro.FechaInicio) : null,
                            entidadFiltro.FechaFin ? new Date(entidadFiltro.FechaFin) : null
                        ]}
                        onChange={(val) => {
                            // val es [Date | null, Date | null] proporcionado por Mantine
                            const [inicio, fin] = val;

                            // Transformamos a formato ISO (ej: "2026-01-19T04:33:29.596Z")
                            // Usamos startOf y endOf para cubrir el rango completo del día
                            const fechaInicioISO = inicio
                                ? dayjs(inicio).startOf('day').toISOString()
                                : '';

                            const fechaFinISO = fin
                                ? dayjs(fin).endOf('day').toISOString()
                                : '';

                            // Actualizamos tu estado con las cadenas de texto formateadas
                            manejarCambioValorFiltro('FechaInicio', fechaInicioISO);
                            manejarCambioValorFiltro('FechaFin', fechaFinISO);
                        }}
                        clearable
                    />
                </Grid.Col>
                <Grid.Col span={{ base: 12, md: 3 }}>
                    <Select
                        label="Proyecto"
                        placeholder="Seleccionar proyecto..."
                        value={
                            entidadFiltro.ProyectoId != null
                                ? String(entidadFiltro.ProyectoId)
                                : null
                        }
                        required={!!esAdmin}
                        disabled={!esAdmin && proyectosFiltrados.length <= 1}
                        onChange={(value) => {

                            manejarCambioValorFiltro(
                                "ProyectoId",
                                value ? value : ''
                            );

                            if (esAdmin) {
                                const proyecto = Proyectos.find(
                                    p => p.Id === Number(value)
                                );

                                manejarCambioValorFiltro(
                                    "Identificacion",
                                    proyecto?.Cliente?.Identificacion || ''
                                );
                            }
                        }}
                        comboboxProps={{ zIndex: 1000000 }}
                        data={
                            proyectosFiltrados.map(p => ({
                                value: p.Id!.toString(),
                                label: p.Nombre
                            }))
                        }
                        clearable={!!esAdmin || proyectosFiltrados.length > 1}
                        searchable
                        size="xs"
                    />

                    {/* <Select
                    label="Cliente"
                    placeholder="Seleccionar cliente..."
                    value={entidadFiltro.Identificacion ? String(entidadFiltro.Identificacion) : null}
                    onChange={(value) => {
                        manejarCambioValorFiltro("Identificacion", value ? value : '')
                    }}
                    data={clientes.map(cliente => ({
                        value: cliente.Identificacion.toString(),
                        label: cliente.Nombre
                    }))}
                    clearable
                    required
                    searchable
                    size="xs"
                /> */}
                </Grid.Col>
                {esAdmin && (
                    <Grid.Col span={{ base: 12, md: 2 }}>
                        <TextInput
                            label="Código Tarea"
                            placeholder="Código Tarea"
                            size="xs"
                            value={entidadFiltro.TareaId || ""}
                            onChange={(event) => {
                                const val = event.currentTarget.value;
                                // Solo permitir números
                                if (/^\d*$/.test(val)) {
                                    manejarCambioValorFiltro("TareaId", val ? Number(val) : null);
                                }
                            }}
                        />
                    </Grid.Col>
                )}
                <Grid.Col span={{ base: 12, md: 2 }}>
                    <Select
                        label="Tipo Tarea"
                        placeholder="Seleccionar tipo..."
                        value={entidadFiltro.TipoServicioId != null
                            ? String(entidadFiltro.TipoServicioId)
                            : null}
                        onChange={(value) => {
                            manejarCambioValorFiltro("TipoServicioId", value ? parseInt(value) : null)
                        }}
                        comboboxProps={{ zIndex: 1000000 }}
                        data={tiposTareaDisponibles.map(c => ({
                            value: c.Id.toString(),
                            label: c.Nombre,
                        }))}
                        disabled={tipoTareaFijo}
                        clearable={!tipoTareaFijo}
                        size="xs"
                    />
                </Grid.Col>
                <Grid.Col span={{ base: 12, md: 2 }}>
                    <TextInput
                        label="Código Equipo"
                        placeholder="Código Equipo"
                        name="CodigoEquipo"
                        size="xs"
                        maxLength={50}
                        value={entidadFiltro.CodigoEquipo || ""}
                        onChange={(event) => {
                            manejarCambioValorFiltro("CodigoEquipo", event.currentTarget.value)
                        }}
                    />
                </Grid.Col>
                <Grid.Col
                    span={{ base: 12, md: esAdmin ? 3 : 2 }}
                    style={{
                        display: "flex",
                        alignItems: "flex-end",
                        marginLeft: "auto",
                        gap: "0.5rem",
                    }}
                >
                    <Button
                        color="primary"
                        variant="light"
                        size="compact-sm"
                        fullWidth
                        radius="lg"
                        rightSection={<IconClearAll opacity={1} size={12} />}
                        onClick={() => { limpiarFiltro(); }}
                    >
                        <Text fw={500} fz={11}>Limpiar</Text>
                    </Button>
                    <Button
                        color="primary"
                        variant="filled"
                        size="compact-sm"
                        fullWidth
                        radius="lg"
                        onClick={() => consultarVisitasCliente(entidadFiltro)}
                        rightSection={<IconSearch opacity={1} size={12} />}
                    >
                        <Text fw={500} fz={11}>Consultar</Text>
                    </Button>

                </Grid.Col>
            </Grid>
        </Box>
    );
};

export default React.memo(FiltroGestionArchivosVista);
